import { CREATE_GROUP_FORM } from '@/constants/forms';
import IdGeneratorService from '@/services/id-generator.service';
import { UseFormReturn } from 'react-hook-form';
import ProgramCard from './ProgramCard';
import { CreateGroupRequest } from '../../views/AddGroupScreen';
import { TextBodySmall, TextLabelMedium2 } from '@/components/text/Texts';
import { Column } from '@/components/container/Container';
import CustomInputTextarea from '@/components/form-inputs/CustomInputTextarea';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';

type Props = {
	activeIndex: number;
	form: UseFormReturn<CreateGroupRequest>;
};

const programs = [
	{
		id: 1, active: true,
		title: 'Travel Program + Concierge',
		description: 'Simplify transportation and gain insights by allowing employees to expense rides directly through the Xpress app, while also enabling easy ride requests for employees, customers, and guests—no app needed.',
		image: '/images/svg/blue-car.svg',
	},
	{
		id: 2, active: false,
		title: 'Meal Program (Coming Soon)',
		description: 'Simplify the process for employees to expense meals directly through the Xpress Eats app, while gaining valuable insights into your team\'s dining expenses.',
		image: '/images/svg/eats.svg',
	},
];

const AddGroupForm = ({ form, activeIndex }: Props) => {
	const formName = new IdGeneratorService(CREATE_GROUP_FORM).formId;
	const selectedProgramId = form.watch('ProgramId');

	const selectProgramPage = (
		<div className="space-y-5">
			<div className="grid grid-cols-1 md:grid-cols-2 md:space-x-4 space-y-4 md:space-y-0">
				{programs.map((program) => {
					const isChecked = program.id === selectedProgramId;

					return (
						<div key={program.title}>
							<ProgramCard
								{...program}
								checked={isChecked}
								setChecked={(checked) => form.setValue('ProgramId', checked ? program.id : 0)}
							/>
						</div>
					);
				})}
			</div>
			<Column>
				<TextLabelMedium2 text="Creating custom travel programs" className="text-onSurfaceVariant" />
				<TextBodySmall className="text-onSurfaceVariant">
					If you would like more controls, create your own unique program
					under each service. By enabling these services you agree to the{' '}
					<span className="text-info">Travel</span> and{' '}
					<span className="text-info">Concierge</span> terms of service.
				</TextBodySmall>
			</Column>
		</div>
	);

	const groupDetailsPage = (
		<Column className="gap-[26px] pt-6 pb-3 tablet:gap-5 tablet:pt-4 tablet:pb-2">
			<Column className="gap-8 tablet:gap-6">
				<InputController id="Name" control={form.control}>
					<TextInput
						label="Name"
						placeholder="For example, a team name or department or role."
						containerCls="w-[calc(50%-12px)] tablet:w-full"
					/>
				</InputController>

				<CustomInputTextarea
					id="Description"
					control={form.control}
					formName={formName || ''}
					label="Description"
					placeholder="Briefly describe who belongs in this group."
					rows={3}
				/>
			</Column>
			<Column>
				<TextBodySmall
					className="text-onSurfaceVariant"
					text="Groups let you define access for specific people."
				/>
				<TextBodySmall
					className="text-onSurfaceVariant"
					text="Make sure the description is clear to other administrators of this account, so they know who belongs in this group."
				/>
			</Column>
		</Column>
	);

	return activeIndex === 0 ? selectProgramPage : groupDetailsPage;
};

export default AddGroupForm;
